<script>
import {ref, watch} from "vue";
import useSharedSelectData from "@/use/SharedSelectData";
import StrBankAutocomplete from "@/components/StrBankAutocomplete.vue";
import StrCategoryAutocomplete from "@/components/StrCategoryAutocomplete.vue";

export default {
    name: "PaySlipAnnexFilter",
    components: {StrCategoryAutocomplete, StrBankAutocomplete},
    props: {
        value: Object,
    },
    setup(props, {emit}) {
        const {workerPhases, transferModes, profiles, sex, paySlipPeriodicities} = useSharedSelectData();
        const filter = ref({});
        const panelModel = ref(0);
        
        
        watch(() => props.value, () => {
            filter.value = props.value;
        }, {immediate: true});
        
        watch(() => filter.value, () => {
            emit('input', filter.value);
        }, {deep: true});
        
        return {
            filter,
            panelModel,
            workerPhases,
            transferModes,
            profiles,
            sex,
            paySlipPeriodicities
        }
    }
}
</script>

<template>
    <div>
        <v-expansion-panels v-model="panelModel" dense focusable>
            <v-expansion-panel>
                <v-expansion-panel-header>
                    <strong>{{ $t('common.pay') }}</strong>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="mr-n5 ml-n5">
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <w-month-picker
                                    v-model="filter.month"
                                    :label="$t('common.month')"
                                ></w-month-picker>
                            </v-col>
                            <v-col cols="12">
                                <w-text-field
                                    v-model="filter.name"
                                    :label="$t('common.name')"
                                ></w-text-field>
                            </v-col>
                            <v-col cols="12">
                                <str-step-select
                                    v-model="filter.lastState"
                                    :label="$t('common.validation')"
                                    module="PAY_SLIP_ANNEX"
                                    by-name
                                ></str-step-select>
                            </v-col>
                            <v-col cols="12">
                                <w-enum-select
                                    v-model="filter.closed"
                                    :items="[
                                        {id: null, name: $t('common.all')},
                                        {id: true, name: $t('common.locked')},
                                        {id: false, name: $t('common.open')},
                                    ]"
                                    :label="$t('common.state')"
                                    not-clearable
                                />
                            </v-col>
                            <v-col cols="12">
                                <str-worker-autocomplete
                                    v-model="filter.workerId"
                                    :label="$t('module.worker')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-data-select-autocomplete
                                    v-model="filter.tagId"
                                    type="PAY_SLIP_TAG"
                                    show-default-label
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-enum-select
                                    v-model="filter.transferMode"
                                    :items="transferModes"
                                    :label="$t('worker.transfer_mode')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <str-bank-autocomplete
                                    v-model="filter.bankId"
                                    :label="$t('worker.bank')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-enum-select
                                    v-model="filter.profile"
                                    :items="profiles"
                                    :label="$t('worker.profile')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <str-category-autocomplete
                                    v-model="filter.categoryId"
                                    :label="$t('module.wire_rack')"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
                <v-expansion-panel-header>
                    <strong>{{ $t('module.worker') }}</strong>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="mr-n5 ml-n5">
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <w-enum-select
                                    v-model="filter.phase"
                                    :items="workerPhases"
                                    :label="$t('worker.status')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <organizational-chart-select
                                    v-model="filter.organizationalchartIds"
                                    :label="$t('module.organizational_chart')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <str-occupation-autocomplete
                                    v-model="filter.occupationId"
                                    :label="$t('module.occupation')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-data-select-autocomplete
                                    v-model="filter.workplaceId"
                                    type="WORKER_WORKPLACE"
                                    show-default-label
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-data-select-autocomplete
                                    v-model="filter.companyId"
                                    type="WORKER_COMPANY"
                                    show-default-label
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-data-select-autocomplete
                                    v-model="filter.stateId"
                                    type="WORKER_STATE"
                                    show-default-label
                                />
                            </v-col>
                            <v-col cols="12">
                                <str-cnas-agcency-autocomplete
                                    v-model="filter.cnasAgencyId"
                                    :label="$t('module.cnas_agency')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-enum-select
                                    v-model="filter.sex"
                                    :items="sex"
                                    :label="$t('common.sex')"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
                <v-expansion-panel-header>
                    <strong>{{ $t('common.period') }}</strong>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="mr-n5 ml-n5">
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <w-enum-select
                                    v-model="filter.periodicity"
                                    :items="paySlipPeriodicities"
                                    :label="$t('common.period')"
                                />
                            </v-col>
                            <v-col cols="12" v-if="filter.periodicity === 'SPECIFIC'">
                                <w-month-picker
                                    v-model="filter.startMonth"
                                    :label="$t('common.from')"
                                />
                            </v-col>
                            <v-col cols="12" v-if="filter.periodicity === 'SPECIFIC'">
                                <w-month-picker
                                    v-model="filter.endMonth"
                                    :label="$t('common.to')"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
        
        <input type="hidden"/>
    </div>
</template>